import { Box, Flex } from "@mantine/core";
import {
  DateTimePicker as MantineDateTimePicker,
  type DateTimePickerProps as MantineDateTimePickerProps,
} from "@mantine/dates";
import { IconCalendarEvent } from "@tabler/icons-react";
import clsx from "clsx";
import { useId } from "react";
import {
  Controller,
  type Control,
  type ControllerProps,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { FormErrorBox } from "@/components/ui/FormErrorBox/FormErrorBox";
import styles from "./DateTimePicker.module.scss";

type ControllerRules<T extends FieldValues = FieldValues> = ControllerProps<T, Path<T>>["rules"];

export type DateTimePickerProps<T extends FieldValues = FieldValues> = Omit<
  MantineDateTimePickerProps,
  "value" | "onChange" | "defaultValue"
> & {
  name: string;
  rules?: ControllerRules<T>;
  control?: Control<T>;
  optionalLabel?: boolean;
  required?: boolean;
  rightSection?: React.ReactNode;
  onValueChange?: (value: Date | null) => void;
};

export function DateTimePicker<T extends FieldValues = FieldValues>({
  name,
  rules,
  popoverProps,
  id,
  control,
  optionalLabel,
  required,
  label,
  rightSection = <IconCalendarEvent size={20} stroke={2} color="var(--icon-default)" />,
  onValueChange,
  ...rest
}: DateTimePickerProps<T>) {
  const generatedId = useId();

  return (
    <Controller
      name={name as Path<T>}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMessage = fieldState.error?.message;
        const hasError = !!errorMessage;

        return (
          <Box>
            {label && (
              <Flex className={styles.labelRow} align={"center"} justify={"space-between"}>
                <label htmlFor={id ?? generatedId} className={styles.label}>
                  {label}
                  {required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
                </label>

                {optionalLabel && <span className={styles.optional}>Optional</span>}
              </Flex>
            )}
            <MantineDateTimePicker
              {...rest}
              id={id ?? generatedId}
              popoverProps={{ withinPortal: true, ...popoverProps }}
              radius="md"
              leftSection={null}
              rightSection={rightSection}
              value={(field.value as Date | null) ?? null}
              onChange={(val) => {
                field.onChange(val);
                onValueChange?.(val as Date | null);
              }}
              onBlur={field.onBlur}
              classNames={{
                input: clsx(hasError && styles.dateTimePickerError),
              }}
              error={false}
            />
            {typeof errorMessage === "string" && <FormErrorBox message={errorMessage} mt="6" />}
          </Box>
        );
      }}
    />
  );
}
